/**
 * Date converter utility functions
 */

/**
 * Converts a date to the format "Aug 13, 2025"
 * @param date - Date to convert (can be Date object, string, or number)
 * @returns Formatted date string in "Aug 13, 2025" format
 */
export const formatDateToReadable = (date: Date | string | number): string => {
  if (!date) {
    return '';
  }

  const dateObj = new Date(date);
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  };

  return dateObj.toLocaleDateString('en-US', options);
};

/**
 * Converts a date to the format "Aug 13, 2025" with time
 * @param date - Date to convert (can be Date object, string, or number)
 * @returns Formatted date string in "Aug 13, 2025, 2:30 PM" format
 */
export const formatDateWithTime = (date: Date | string | number): string => {
  if (!date) {
    return '';
  }

  const dateObj = new Date(date);
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  };

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };

  const formattedDate = dateObj.toLocaleDateString('en-US', dateOptions);
  const formattedTime = dateObj.toLocaleTimeString('en-US', timeOptions);

  return `${formattedDate}, ${formattedTime}`;
};

/**
 * Converts a date to relative time (e.g., "2 hours ago", "3 days ago")
 * @param date - Date to convert (can be Date object, string, or number)
 * @returns Relative time string
 */
export const formatRelativeTime = (date: Date | string | number): string => {
  if (!date) {
    return '';
  }

  const dateObj = new Date(date);
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const now = new Date();
  const diffInMs = now.getTime() - dateObj.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  } else {
    return formatDateToReadable(date);
  }
};
